import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { reportAPI } from '../../../services/api';
import { inFlightReportScopeFetches, reportScopeDataCache } from './reportScopeDataCache';
import { LatestClosedPeriod, ReportTarget } from '../../../types/api';

interface CostCenter {
  Id: string;
  Name: string;
  Number?: number;
  IsActive?: boolean;
  Items?: Array<{
    Id: string;
    Name: string;
    ShortName: string;
    IsActive: boolean;
    CreatedUtc: string;
    CostCenterId: string;
  }>;
}

interface CompanySettings {
  Name: string;
  CorporateIdentityNumber?: string;
  CurrencyCode?: string;
  CountryCode?: string;
  Address1?: string;
  PostalCode?: string;
  City?: string;
  Phone?: string;
  Email?: string;
  Website?: string;
}

interface ReportScopeDataContextType {
  costCenters: CostCenter[];
  costCentersError: string | null;
  companySettings: CompanySettings | null;
  companySettingsError: string | null;
  latestClosedPeriod: LatestClosedPeriod | null;
  latestClosedPeriodError: string | null;
  isLoading: boolean;
  isRedirecting: boolean;
  error: string | null;
}

const ReportScopeDataContext = createContext<ReportScopeDataContextType | undefined>(undefined);

export { ReportScopeDataContext };

export const ReportScopeDataProvider: React.FC<{ children: React.ReactNode; reloadKey?: number; enabled?: boolean; currentProfileKey?: string; currentTarget?: ReportTarget | null }> = ({
  children,
  reloadKey,
  enabled = true,
  currentProfileKey = '',
  currentTarget = null,
}) => {
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [costCentersError, setCostCentersError] = useState<string | null>(null);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [companySettingsError, setCompanySettingsError] = useState<string | null>(null);
  const [latestClosedPeriod, setLatestClosedPeriod] = useState<LatestClosedPeriod | null>(null);
  const [latestClosedPeriodError, setLatestClosedPeriodError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentTargetType = currentTarget?.type ?? null;
  const targetCacheKey = currentTarget ? `${currentTarget.type}:${currentTarget.id}` : (currentProfileKey || 'default-profile');
  const previousTargetCacheKeyRef = useRef(targetCacheKey);
  const previousReloadKeyRef = useRef(reloadKey);

  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      try {
        if (!enabled) {
          if (!isCancelled) {
            setCostCenters([]);
            setCostCentersError(null);
            setCompanySettings(null);
            setCompanySettingsError(null);
            setLatestClosedPeriod(null);
            setLatestClosedPeriodError(null);
            setIsLoading(false);
            setIsRedirecting(false);
          }
          return;
        }

        const requestKey = targetCacheKey;
        const targetChanged = previousTargetCacheKeyRef.current !== targetCacheKey;
        const reloadChanged = previousReloadKeyRef.current !== reloadKey;
        const shouldBypassCache = !targetChanged && reloadChanged;

        previousTargetCacheKeyRef.current = targetCacheKey;
        previousReloadKeyRef.current = reloadKey;

        if (!shouldBypassCache) {
          const cached = reportScopeDataCache.get(requestKey);
          if (cached) {
            if (!isCancelled) {
              setError(null);
              setCostCenters(cached.costCenters);
              setCostCentersError(cached.costCentersError);
              setCompanySettings(cached.companySettings);
              setCompanySettingsError(cached.companySettingsError);
              setLatestClosedPeriod(cached.latestClosedPeriod);
              setLatestClosedPeriodError(cached.latestClosedPeriodError);
              setIsLoading(false);
              setIsRedirecting(false);
            }
            return;
          }
        }

        if (!isCancelled) {
          setIsLoading(true);
          setError(null);
        }

        let request = inFlightReportScopeFetches.get(requestKey);
        if (!request) {
          request = (async () => {
            const [costCentersResult, latestClosedPeriodResult] = await Promise.all([
              reportAPI.getCostCenters()
                .then((costCentersResponse) => ({ costCenters: costCentersResponse, error: null as string | null }))
                .catch((costCentersFetchError) => ({
                  costCenters: [] as CostCenter[],
                  error: costCentersFetchError instanceof Error
                    ? costCentersFetchError.message
                    : 'Kunde inte läsa resultatenheter just nu.',
                })),
              reportAPI.getLatestClosedPeriod()
                .then((period) => ({ period, error: null as string | null }))
                .catch((latestClosedPeriodFetchError) => ({
                  period: null,
                  error: latestClosedPeriodFetchError instanceof Error
                    ? latestClosedPeriodFetchError.message
                    : 'Kunde inte läsa senaste stängda period från Spiris.',
                })),
            ]);
            if (currentTargetType === 'group') {
              return {
                costCenters: Array.isArray(costCentersResult.costCenters) ? costCentersResult.costCenters : [],
                costCentersError: costCentersResult.error,
                companySettings: null,
                companySettingsError: null,
                latestClosedPeriod: latestClosedPeriodResult.period,
                latestClosedPeriodError: latestClosedPeriodResult.error,
              };
            }

            const companySettingsResult = await reportAPI.getCompanySettings()
              .then((companySettingsResponse) => ({ companySettings: companySettingsResponse, error: null as string | null }))
              .catch((companySettingsFetchError) => ({
                companySettings: null,
                error: companySettingsFetchError instanceof Error
                  ? companySettingsFetchError.message
                  : 'Kunde inte läsa bolagsuppgifter just nu.',
              }));
            return {
              costCenters: Array.isArray(costCentersResult.costCenters) ? costCentersResult.costCenters : [],
              costCentersError: costCentersResult.error,
              companySettings: companySettingsResult.companySettings,
              companySettingsError: companySettingsResult.error,
              latestClosedPeriod: latestClosedPeriodResult.period,
              latestClosedPeriodError: latestClosedPeriodResult.error,
            };
          })();
          inFlightReportScopeFetches.set(requestKey, request);
        }

        const snapshot = await request;
        reportScopeDataCache.set(requestKey, snapshot);

        if (!isCancelled) {
          setCostCenters(snapshot.costCenters);
          setCostCentersError(snapshot.costCentersError);
          setCompanySettings(snapshot.companySettings);
          setCompanySettingsError(snapshot.companySettingsError);
          setLatestClosedPeriod(snapshot.latestClosedPeriod);
          setLatestClosedPeriodError(snapshot.latestClosedPeriodError);
        }
      } catch (err) {
        if (!isCancelled) {
          setIsRedirecting(false);
          if ((err as { suppressUserError?: boolean } | null)?.suppressUserError) {
            return;
          }
          if (axios.isAxiosError(err)) {
            const errorMessage = (err.response?.data as { error?: string } | undefined)?.error;
            if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('spiris')) {
              setError('Spiris saknas. Kontakta admin.');
              return;
            }
          }
          setError(err instanceof Error ? err.message : 'Kunde inte hämta data.');
        }
      } finally {
        const requestKey = targetCacheKey;
        inFlightReportScopeFetches.delete(requestKey);
        if (!isCancelled) {
          setIsLoading(false);
          setIsRedirecting(false);
        }
      }
    };

    if (!enabled) {
      setIsLoading(false);
      setIsRedirecting(false);
      return;
    }

    fetchData();
    return () => {
      isCancelled = true;
    };
  }, [reloadKey, enabled, currentTargetType, targetCacheKey]);

  const contextValue = useMemo(() => ({
    costCenters,
    costCentersError,
    companySettings,
    companySettingsError,
    latestClosedPeriod,
    latestClosedPeriodError,
    isLoading,
    isRedirecting,
    error,
  }), [companySettings, companySettingsError, costCenters, costCentersError, error, isLoading, isRedirecting, latestClosedPeriod, latestClosedPeriodError]);

  return (
    <ReportScopeDataContext.Provider value={contextValue}>
      {children}
    </ReportScopeDataContext.Provider>
  );
};

export const useReportScopeData = () => {
  const context = useContext(ReportScopeDataContext);
  if (!context) {
    throw new Error('useReportScopeData must be used within ReportScopeDataProvider');
  }
  return context;
};